'use client';

import React from 'react';

import Navigation from '../navigation/Navigation';
import Slider from '../slider';
import RichText from '../richtext';
import ContactList from '../contact-list';
import Socials from '../socials';
import { FlexiPassDigitalKeys, RemotelockDigitalKeys } from '../digital-keys';

import { Contact } from '@/components/contact';

import { PageSectionType, Settings, CheckOutSettings } from '@/models';
import { Amenities } from '../amenities';
import { Address } from '../address';
import { usePreviewState } from '@/context/PreviewStateProvider';
import ReservationPreview from './ReservationPreview';
import { SectionImage } from '../core/display/SectionImage';

interface PageSectionsProps {
  locale: string;
  company: string;
  guestBookSlug: string;
  pageSlug: string;
  settings: Settings;
}

const sectionTypeComponents: {
  [key in Exclude<PageSectionType, 'text'>]: React.ElementType;
} = {
  navigation: Navigation,
  slider: Slider,
  richtext: RichText,
  contacts: ContactList,
  socials: Socials,
  image: SectionImage,
  flexipass_digital_keys: FlexiPassDigitalKeys,
  remotelock_digital_keys: RemotelockDigitalKeys,
};

/**
 * Preview-aware version of PageSections that responds to real-time content updates
 * Renders sections dynamically based on preview state from context
 */
const PreviewAwarePageSections = ({
  locale,
  company,
  guestBookSlug,
  pageSlug,
  settings,
}: PageSectionsProps) => {
  const previewData = usePreviewState();
  const reservation = previewData?.previewData.reservation || null;
  const reservations = reservation ? [reservation] : [];
  const checkInSettings = previewData?.previewData.checkInSettings || null;
  const sections = previewData?.previewData.content?.sections || [];

  const checkOutSettings = { enabled: false } as CheckOutSettings;
  const showContactSection = !sections.length;
  const showAmenities = !!(
    !sections.length &&
    (settings.property.amenitiesV2?.amenities?.length ||
      settings.property.amenitiesV2?.customAmenities?.length ||
      settings.property.amenities?.length)
  );
  const showAddress = !!(!sections.length && settings.property.address);

  return (
    <>
      <ReservationPreview
        company={company}
        guestBookSlug={guestBookSlug}
        pageSlug={pageSlug}
        locale={locale}
        settings={settings}
        sections={sections}
        reservation={reservation}
        reservations={reservations}
        guestId={'1'}
        preview={true}
        checkInSettings={checkInSettings}
        checkOutSettings={checkOutSettings}
      ></ReservationPreview>

      {showContactSection && (
        <Contact
          sms={{ value: settings.whistle_guest_sms_number }}
          phone={{ value: settings.property.phone }}
          email={{ value: settings.property.email }}
        />
      )}

      {showAmenities && (
        <Amenities
          amenities={settings.property.amenities}
          amenitiesV2={settings.property.amenitiesV2}
          mobileOnly
        />
      )}

      {showAddress && (
        <Address
          propertyName={settings.property.name}
          address={settings.property.address}
          mobileOnly
          primaryColor={settings?.whistle_guest_primary_button_color}
        />
      )}

      {sections.map((section) => {
        if (!section.settings.visible || section.type === 'text') {
          return null;
        }

        const SectionComponent = sectionTypeComponents[section.type];
        // Digital key sections are rendered in ReservationSection component
        if (
          !SectionComponent ||
          section.type === 'flexipass_digital_keys' ||
          section.type === 'remotelock_digital_keys'
        )
          return null;

        return (
          <SectionComponent
            key={section.id}
            company={company}
            guestBookSlug={guestBookSlug}
            section={section}
            settings={settings}
            preview={true}
            isKioskMode={false}
          />
        );
      })}
    </>
  );
};

export default PreviewAwarePageSections;
